import { IApiSmpMedium } from "@smp/api";
import { PostDisplayKindEnum, PostStatusEnum } from "@smp/graphql";
import { dataArrayAddOrRemove } from "@smp/utils";
import produce from "immer";
import { createContext, PropsWithChildren, useContext } from "react";

import { useContextPosts } from "../posts/posts";
import { IContextFeedFilters } from "./feed_filters.types";

const ContextFeedFilters = createContext<IContextFeedFilters>(
  null as unknown as IContextFeedFilters,
);

export const ProviderFeedFilters = ({ children }: PropsWithChildren<{}>) => {
  const {
    actions: { changeFeedFilters },
    state: { filtersFeed },
  } = useContextPosts();

  const changeScheduledFor = (gte: string, lte: string) => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.scheduledFor.gte = gte;
        draft.scheduledFor.lte = lte;
      }),
    );
  };

  const allTypes = Object.values(PostDisplayKindEnum);

  const isEveryTypeSelected = allTypes.every((value) =>
    filtersFeed.type.includes(value),
  );

  const changeMediumAllFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.medium = [];
      }),
    );
  };

  const changeMediumFilter = (medium: IApiSmpMedium) => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.medium = dataArrayAddOrRemove(draft.medium, medium);
      }),
    );
  };

  const changeSortByDescending = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.sortByDescending = !draft.sortByDescending;
      }),
    );
  };

  const changePostId = (postId?: number | null) => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.id = postId;
      }),
    );
  };

  const changeSocialPageGroupFilter = (socialPageGroupIds: number[]) => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.socialPageGroupIds = socialPageGroupIds;
      }),
    );
  };

  const changePageConfigFilter = (value: number) => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.pageConfigIds = dataArrayAddOrRemove(draft.pageConfigIds, value);
      }),
    );
  };

  const changePageConfigFilterReset = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.pageConfigIds = [];
      }),
    );
  };

  const changeStatusAllFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.status.push(...Object.values(PostStatusEnum));
      }),
    );
  };

  const changeStatusArchivedFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.status = [PostStatusEnum.Archived];
      }),
    );
  };

  const changeStatusErrorFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.status = [PostStatusEnum.Error];
      }),
    );
  };

  const changeStatusFavoriteFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.favorite = !draft.favorite;
      }),
    );
  };

  const changeStatusPublishedFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.status = [PostStatusEnum.Published];
      }),
    );
  };

  const changeStatusScheduledFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.status = [PostStatusEnum.Scheduled];
      }),
    );
  };

  const changeTypeAllFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        const statusToApply = isEveryTypeSelected ? false : true;
        if (statusToApply) {
          draft.type.push(...Object.values(PostDisplayKindEnum));
        } else {
          draft.type = [];
        }
      }),
    );
  };

  const changeTypePostFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.type = dataArrayAddOrRemove(draft.type, PostDisplayKindEnum.Feed);
      }),
    );
  };

  const changeTypeReelsFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.type = dataArrayAddOrRemove(draft.type, PostDisplayKindEnum.Clip);
      }),
    );
  };

  const changeTypeStoriesFilter = () => {
    changeFeedFilters(
      produce(filtersFeed, (draft) => {
        draft.type = dataArrayAddOrRemove(
          draft.type,
          PostDisplayKindEnum.Story,
        );
      }),
    );
  };

  return (
    <ContextFeedFilters.Provider
      value={{
        actions: {
          changeMediumAllFilter,
          changeMediumFilter,
          changePageConfigFilter,
          changePageConfigFilterReset,
          changePostId,
          changeScheduledFor,
          changeSocialPageGroupFilter,
          changeSortByDescending,
          changeStatusAllFilter,
          changeStatusArchivedFilter,
          changeStatusErrorFilter,
          changeStatusFavoriteFilter,
          changeStatusPublishedFilter,
          changeStatusScheduledFilter,
          changeTypeAllFilter,
          changeTypePostFilter,
          changeTypeReelsFilter,
          changeTypeStoriesFilter,
        },
      }}
    >
      {children}
    </ContextFeedFilters.Provider>
  );
};

export const useContextFeedFilters = () => useContext(ContextFeedFilters);
